/**
 * useNameMode — operator-controlled "how do we render player names?"
 *
 * Operators creating players enter a `first_name` AND a separate "Display
 * Name" (`nick_name`). The squad / Playing-XI selection screens show
 * dozens of player buttons at once, and different scoring teams prefer
 * different conventions:
 *
 *   - Some prefer `first_name + last_name` (formal cricinfo-style)
 *   - Some prefer `nick_name` (shorter, easier to spot on a dense grid)
 *
 * This hook stores the choice in localStorage so it persists across
 * navigation and sessions. Both Squad.jsx and PlayingXI.jsx call this
 * and share the same toggle value, so flipping it on one page applies
 * everywhere.
 *
 * Default is "display" — matches the BBB coding interface which now
 * always renders the display name.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "zcd_player_name_mode";

// "display" = nick_name first, falls back to first+last
// "first"   = first+last first, falls back to nick_name
const DEFAULT_MODE = "display";

/**
 * playerDisplayName(p) — canonical "what name do we show for a player?"
 *
 * Use this anywhere a player name is rendered (lists, scorecards,
 * dropdowns, modals, …). The Squad / Playing-XI screens have an
 * operator toggle and use the useNameMode hook below; everywhere
 * else, default to display-name semantics via this helper.
 *
 * Preference order (never returns blank):
 *   1. p.nick_name   — operator-entered "Display Name"
 *   2. p.name        — composite from API serializers that ship a `name`
 *   3. p.first_name + p.last_name
 *   4. p.id
 *   5. "Unknown"
 *
 * 2026-06-01 — the jersey number is now suffixed in parens when set
 * (e.g. "Virat (18)"). Founder ask: jersey visible across every
 * player-name surface in the SPA. Players without a jersey number
 * fall through unchanged. Callers that want the raw name without
 * the jersey suffix can pass `{ withJersey: false }`.
 */
function _withJersey(baseName, p, withJersey) {
  if (!withJersey || !p) return baseName;
  const jersey =
    p.jersey_number != null && String(p.jersey_number).trim() !== ""
      ? _formatJersey(String(p.jersey_number).trim())
      : null;
  return jersey ? `${baseName} (${jersey})` : baseName;
}

// Legacy CSV uploads stored Excel-float cells as "72.0" strings on
// PackPlayer.jersey_number; the buyer's cloned Player row inherits that
// literal. Coerce numeric-looking strings back to int display so old
// packs render "72" instead of "72.0" without a data migration.
function _formatJersey(raw) {
  const s = raw.trim();
  if (!s) return s;
  const n = Number(s);
  if (Number.isFinite(n) && Number.isInteger(n)) return String(n);
  // Trailing ".0" (or ".00…") — strip; keep other decimals as-is.
  if (/^-?\d+\.0+$/.test(s)) return s.replace(/\.0+$/, "");
  return s;
}

export function playerDisplayName(p, { withJersey = true } = {}) {
  if (!p) return "Unknown";
  const nick = (p.nick_name || "").trim();
  if (nick) return _withJersey(nick, p, withJersey);
  const composite = (p.name || "").trim();
  if (composite) return _withJersey(composite, p, withJersey);
  const first = (p.first_name || "").trim();
  const last  = (p.last_name  || "").trim();
  const full = [first, last].filter(Boolean).join(" ").trim();
  if (full) return _withJersey(full, p, withJersey);
  return p.id || "Unknown";
}

export function useNameMode() {
  const [mode, setModeState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return (stored === "display" || stored === "first") ? stored : DEFAULT_MODE;
    } catch {
      return DEFAULT_MODE;
    }
  });

  // Cross-tab sync — if a user has the SPA open in two tabs, flipping
  // the mode in one immediately updates the other on focus.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && (e.newValue === "display" || e.newValue === "first")) {
        setModeState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setMode = useCallback((next) => {
    if (next !== "display" && next !== "first") return;
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  /**
   * Render a player's name per the current mode.
   * Handles every quirk: blank/missing fields, players that only have
   * a nick_name, players that only have a first_name, etc. Never
   * returns an empty string — falls all the way back to id.
   */
  const renderName = useCallback((p) => {
    if (!p) return "Unknown";
    const first = (p.first_name || "").trim();
    const last  = (p.last_name  || "").trim();
    const full  = [first, last].filter(Boolean).join(" ").trim();
    if (mode === "first") {
      return full ? _withJersey(full, p, true) : playerDisplayName(p);
    }
    // "display" mode — defer to the canonical helper so this stays in
    // lockstep with everywhere else in the SPA that renders names.
    return playerDisplayName(p);
  }, [mode]);

  return { mode, setMode, renderName };
}

/**
 * <NameModeToggle /> — small inline segmented control. Drop on any page
 * that displays many player names. Self-contained — manages its own
 * state via the hook.
 *
 * Style matches the existing pill-toggle pattern used elsewhere in the
 * SPA (Wallet / BBB activity flow). Theme-aware via the standard
 * bg-bg-secondary / border-border-default tokens.
 */
export function NameModeToggle({ className = "" }) {
  const { mode, setMode } = useNameMode();
  return (
    <div className={`inline-flex items-center rounded-md border border-border-default bg-bg-secondary p-0.5 text-xs ${className}`}>
      <button
        type="button"
        onClick={() => setMode("first")}
        className={`px-3 py-1 rounded transition-colors ${
          mode === "first"
            ? "bg-button-primary text-white"
            : "text-text-secondary hover:text-text-primary"
        }`}
        title="Show first + last name on player buttons"
      >
        First name
      </button>
      <button
        type="button"
        onClick={() => setMode("display")}
        className={`px-3 py-1 rounded transition-colors ${
          mode === "display"
            ? "bg-button-primary text-white"
            : "text-text-secondary hover:text-text-primary"
        }`}
        title="Show display name (nick_name) on player buttons"
      >
        Display name
      </button>
    </div>
  );
}
